(() => {
  'use strict';

  const CONFIG = {
    baseHeight: 10,
    baseDiameter: 7,
    surchargePerCm: 130,
    locale: 'de-DE',
    currency: 'EUR'
  };

  /*
   * Speichert laufende APO-Wiederholungsversuche,
   * damit nicht mehrere Timer gleichzeitig entstehen.
   */
  const apoRetryTimers = new WeakMap();

  /**
   * Formatiert einen Betrag in Cent als Euro.
   */
  const formatMoney = (amountInCents) => {
    return new Intl.NumberFormat(CONFIG.locale, {
      style: 'currency',
      currency: CONFIG.currency
    }).format(amountInCents / 100);
  };

  /**
   * Ermittelt den Produktbereich,
   * zu dem der Konfigurator gehört.
   */
  const findProductSection = (configurator) => {
    return configurator.closest(
      '[data-section-id], .shopify-section, section, main'
    );
  };

  /**
   * Findet das Shopify-Produktformular,
   * das zum jeweiligen Konfigurator gehört.
   */
  const findProductForm = (configurator) => {
    const productSection =
      findProductSection(configurator);

    if (productSection) {
      const form = productSection.querySelector(
        'form[action*="/cart/add"]'
      );

      if (form) {
        return form;
      }
    }

    return document.querySelector(
      'form[action*="/cart/add"]'
    );
  };

  /**
   * Erstellt ein verstecktes Line-Item-Property-Feld,
   * sofern es noch nicht vorhanden ist.
   */
  const createPropertyInput = (
    form,
    propertyName
  ) => {
    if (!form) {
      return null;
    }

    let input = form.querySelector(
      `input[type="hidden"][name="${propertyName}"]`
    );

    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = propertyName;
      form.appendChild(input);
    }

    return input;
  };

  /**
   * Setzt den Wert eines APO-Inputs so,
   * dass APO die Änderung korrekt erkennt.
   */
  const setNativeInputValue = (
    input,
    value
  ) => {
    if (!input) {
      return false;
    }

    const stringValue = String(value);

    /*
     * Keine unnötigen Events auslösen,
     * wenn der richtige Wert bereits gesetzt ist.
     */
    if (String(input.value) === stringValue) {
      return true;
    }

    const nativeValueSetter =
      Object.getOwnPropertyDescriptor(
        HTMLInputElement.prototype,
        'value'
      )?.set;

    if (nativeValueSetter) {
      nativeValueSetter.call(
        input,
        stringValue
      );
    } else {
      input.value = stringValue;
    }

    input.dispatchEvent(
      new Event('input', {
        bubbles: true,
        composed: true
      })
    );

    input.dispatchEvent(
      new Event('change', {
        bubbles: true,
        composed: true
      })
    );

    return true;
  };

  /**
   * Sucht einen APO-Range-Slider zuerst anhand
   * der in APO eingetragenen CSS-Klasse.
   *
   * Als Absicherung wird zusätzlich anhand
   * von Minimum und Maximum gesucht.
   */
  const findApoRangeInput = ({
    configurator,
    cssClass,
    min,
    max
  }) => {
    const productSection =
      findProductSection(configurator);

    const searchRoots = [
      productSection,
      document
    ].filter(Boolean);

    for (const root of searchRoots) {
      /*
       * Fall 1:
       * Die APO-CSS-Klasse befindet sich
       * direkt auf dem Range-Input.
       */
      const directInput = root.querySelector(
        `input.${cssClass}[type="range"]`
      );

      if (directInput) {
        return directInput;
      }

      /*
       * Fall 2:
       * Die APO-CSS-Klasse befindet sich
       * auf dem umgebenden Optionsblock.
       */
      const wrappedInput = root.querySelector(
        `.${cssClass} input[type="range"]`
      );

      if (wrappedInput) {
        return wrappedInput;
      }

      /*
       * Fall 3:
       * Fallback über die APO-Werte.
       */
      // Eigene Maß-Slider niemals als APO-Feld verwenden.
      const candidates = Array.from(root.querySelectorAll(
        `input[type="range"][min="${min}"]`
      )).filter((input) =>
        !input.closest('[data-mass-configurator]') &&
        !input.matches('[data-height-slider], [data-diameter-slider]')
      );
      const exactMatches = candidates.filter((input) =>
        Number(input.max) === Number(max)
      );
      if (exactMatches.length === 1) {
        return exactMatches[0];
      }
      // Bei geändertem Maximum nur ein eindeutig gefundenes Feld nutzen.
      if (candidates.length === 1) {
        return candidates[0];
      }
    }

    return null;
  };

  /**
   * Findet den APO-Höhenregler.
   *
   * Vasen:
   * 10 bis 34 cm
   *
   * Pflanztöpfe:
   * 10 bis 30 cm
   */
  const findApoHeightInput = (
    configurator
  ) => {
    const productType =
      configurator.dataset.productType || '';

    const isPot =
      productType === 'pot' ||
      Boolean(
        configurator.querySelector(
          '[data-diameter-slider]'
        )
      );

    return findApoRangeInput({
      configurator,
      cssClass: 'apo-height-slider',
      min: 10,
      max: isPot ? 30 : 34
    });
  };

  /**
   * Findet den APO-Regler für
   * den Innendurchmesser.
   */
  const findApoDiameterInput = (
    configurator
  ) => {
    return findApoRangeInput({
      configurator,
      cssClass: 'apo-diameter-slider',
      min: 7,
      max: 19
    });
  };

  /**
   * Verwaltet den Wiederholungs-Timer
   * für eine bestimmte APO-Option.
   */
  const setRetryTimer = (
    configurator,
    key,
    callback
  ) => {
    const currentTimers =
      apoRetryTimers.get(configurator) || {};

    if (currentTimers[key]) {
      return;
    }

    currentTimers[key] = window.setTimeout(() => {
      const latestTimers =
        apoRetryTimers.get(configurator) || {};

      delete latestTimers[key];

      apoRetryTimers.set(
        configurator,
        latestTimers
      );

      callback();
    }, 200);

    apoRetryTimers.set(
      configurator,
      currentTimers
    );
  };

  /**
   * Überträgt die Höhe an APO.
   *
   * APO kann verzögert geladen werden.
   * Deshalb wird die Suche bei Bedarf wiederholt.
   */
  const synchronizeApoHeight = (
    configurator,
    height,
    attempt = 0
  ) => {
    const apoHeightInput =
      findApoHeightInput(configurator);

    if (apoHeightInput) {
      setNativeInputValue(
        apoHeightInput,
        height
      );

      return true;
    }

    if (attempt < 30) {
      setRetryTimer(
        configurator,
        'height',
        () => {
          synchronizeApoHeight(
            configurator,
            height,
            attempt + 1
          );
        }
      );

      return false;
    }

    console.warn(
      'Maß-Slider: Der APO-Höhenregler wurde nicht gefunden.'
    );

    return false;
  };

  /**
   * Überträgt den Innendurchmesser an APO.
   *
   * APO kann verzögert geladen werden.
   * Deshalb wird die Suche bei Bedarf wiederholt.
   */
  const synchronizeApoDiameter = (
    configurator,
    diameter,
    attempt = 0
  ) => {
    const apoDiameterInput =
      findApoDiameterInput(configurator);

    if (apoDiameterInput) {
      setNativeInputValue(
        apoDiameterInput,
        diameter
      );

      return true;
    }

    if (attempt < 30) {
      setRetryTimer(
        configurator,
        'diameter',
        () => {
          synchronizeApoDiameter(
            configurator,
            diameter,
            attempt + 1
          );
        }
      );

      return false;
    }

    console.warn(
      'Maß-Slider: Der APO-Regler für den Innendurchmesser wurde nicht gefunden.'
    );

    return false;
  };

  /**
   * Blendet ausschließlich die alte
   * Globo-Größenauswahl aus.
   *
   * Die Farbauswahl bleibt sichtbar.
   */
  const hideLegacyProductElements = (
    configurator
  ) => {
    const productSection =
      findProductSection(configurator);

    if (!productSection) {
      return;
    }

    productSection.classList.add(
      'mass-slider-product-active'
    );

    productSection
      .querySelectorAll(
        '.mass-legacy-size-picker'
      )
      .forEach((element) => {
        element.classList.remove(
          'mass-legacy-size-picker'
        );
      });

    const globoLegends =
      productSection.querySelectorAll(
        '.globo-swatch-product-detail legend.name-option'
      );

    globoLegends.forEach((legend) => {
      const optionName = (
        legend.getAttribute('data-name') ||
        legend.textContent ||
        ''
      )
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

      const isSizeOption =
        optionName === 'größe' ||
        optionName === 'grösse' ||
        optionName === 'size';

      if (!isSizeOption) {
        return;
      }

      const sizeBlock =
        legend.closest('.swatch--gl');

      if (sizeBlock) {
        sizeBlock.classList.add(
          'mass-legacy-size-picker'
        );
      }
    });
  };

  /**
   * Initialisiert einen Maß-Konfigurator.
   */
  const initializeConfigurator = (
    configurator
  ) => {
    /*
     * Falls der Konfigurator bereits initialisiert ist,
     * wird trotzdem erneut synchronisiert.
     *
     * Das ist wichtig, wenn APO seine Felder
     * nachträglich neu in die Seite einsetzt.
     */
    if (
      configurator.dataset.massInitialized ===
      'true'
    ) {
      hideLegacyProductElements(
        configurator
      );

      if (
        typeof configurator.massRefresh ===
        'function'
      ) {
        configurator.massRefresh();
      }

      return;
    }

    const heightSlider =
      configurator.querySelector(
        '[data-height-slider]'
      );

    const diameterSlider =
      configurator.querySelector(
        '[data-diameter-slider]'
      );

    const heightOutput =
      configurator.querySelector(
        '[data-height-output]'
      );

    const diameterOutput =
      configurator.querySelector(
        '[data-diameter-output]'
      );

    const priceOutput =
      configurator.querySelector(
        '[data-calculated-price]'
      );

    const oldPriceProperty =
      configurator.querySelector(
        '[data-configured-price-property]'
      );

    const productForm =
      findProductForm(configurator);

    if (!heightSlider || !productForm) {
      console.warn(
        'Maß-Slider: Regler oder Produktformular wurde nicht gefunden.'
      );

      return;
    }

    /*
     * Die sichtbaren Slider werden nicht direkt
     * als Warenkorb-Eigenschaften übertragen.
     */
    heightSlider.removeAttribute('name');

    if (diameterSlider) {
      diameterSlider.removeAttribute('name');
    }

    if (oldPriceProperty) {
      oldPriceProperty.remove();
    }

    /*
     * Versteckte Warenkorb-Eigenschaften erstellen.
     */
    const heightProperty =
      createPropertyInput(
        productForm,
        'properties[Höhe]'
      );

    const diameterProperty =
      diameterSlider
        ? createPropertyInput(
            productForm,
            'properties[Innendurchmesser]'
          )
        : null;

    const priceProperty =
      createPropertyInput(
        productForm,
        'properties[Konfigurierter Preis]'
      );

    /**
     * Aktualisiert:
     * - sichtbare Werte
     * - sichtbaren Preis
     * - Warenkorb-Eigenschaften
     * - APO-Höhenregler
     * - APO-Durchmesserregler
     */
    const calculatePrice = () => {
      const basePrice = Number(
        configurator.dataset.basePrice || 0
      );

      const surchargePerCm = Number(
        configurator.dataset
          .surchargePerCm ||
          CONFIG.surchargePerCm
      );

      const height = Number(
        heightSlider.value
      );

      const diameter = diameterSlider
        ? Number(diameterSlider.value)
        : CONFIG.baseDiameter;

      const heightSurcharge =
        Math.max(
          0,
          height - CONFIG.baseHeight
        ) * surchargePerCm;

      const diameterSurcharge =
        diameterSlider
          ? Math.max(
              0,
              diameter -
                CONFIG.baseDiameter
            ) * surchargePerCm
          : 0;

      const totalPrice =
        basePrice +
        heightSurcharge +
        diameterSurcharge;

      /*
       * Sichtbare Ausgabe aktualisieren.
       */
      if (heightOutput) {
        heightOutput.textContent =
          String(height);
      }

      if (diameterOutput) {
        diameterOutput.textContent =
          String(diameter);
      }

      if (priceOutput) {
        priceOutput.textContent =
          formatMoney(totalPrice);
      }

      /*
       * Warenkorb-Eigenschaften aktualisieren.
       */
      if (heightProperty) {
        heightProperty.value =
          `${height} cm`;
      }

      if (diameterProperty) {
        diameterProperty.value =
          `${diameter} cm`;
      }

      if (priceProperty) {
        priceProperty.value =
          formatMoney(totalPrice);
      }

      /*
       * Höhe immer mit APO abgleichen.
       *
       * setNativeInputValue verhindert selbst,
       * dass unnötige Events ausgelöst werden,
       * wenn der Wert bereits korrekt ist.
       */
      synchronizeApoHeight(
        configurator,
        height
      );

      /*
       * Nur Pflanztöpfe besitzen
       * einen Durchmesserregler.
       */
      if (diameterSlider) {
        synchronizeApoDiameter(
          configurator,
          diameter
        );
      }
    };

    heightSlider.addEventListener(
      'input',
      calculatePrice
    );

    heightSlider.addEventListener(
      'change',
      calculatePrice
    );

    if (diameterSlider) {
      diameterSlider.addEventListener(
        'input',
        calculatePrice
      );

      diameterSlider.addEventListener(
        'change',
        calculatePrice
      );
    }

    /*
     * Direkt vor dem Absenden nochmals sicherstellen,
     * dass APO und die Warenkorb-Eigenschaften
     * die aktuellen Werte besitzen.
     */
    productForm.addEventListener(
      'submit',
      calculatePrice,
      true
    );

    productForm.addEventListener(
      'click',
      (event) => {
        const submitButton =
          event.target.closest(
            'button[type="submit"], input[type="submit"], [name="add"]'
          );

        if (submitButton) {
          calculatePrice();
        }
      },
      true
    );

    hideLegacyProductElements(
      configurator
    );

    configurator.massRefresh =
      calculatePrice;

    configurator.dataset.massInitialized =
      'true';

    calculatePrice();
  };

  /**
   * Initialisiert alle Konfiguratoren.
   */
  const initializeAll = () => {
    document
      .querySelectorAll(
        '[data-mass-configurator]'
      )
      .forEach(initializeConfigurator);
  };

  document.addEventListener(
    'DOMContentLoaded',
    initializeAll
  );

  document.addEventListener(
    'shopify:section:load',
    initializeAll
  );

  /**
   * Shopify und APO können Elemente
   * nachträglich in die Seite einsetzen.
   */
  let observerScheduled = false;

  const observer = new MutationObserver(() => {
    if (observerScheduled) {
      return;
    }

    observerScheduled = true;

    window.requestAnimationFrame(() => {
      observerScheduled = false;

      initializeAll();

      document
        .querySelectorAll(
          '[data-mass-configurator]'
        )
        .forEach(
          hideLegacyProductElements
        );
    });
  });

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );
})();